import React from 'react';
import {
  Box,
  TextField,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  ListItemText,
  OutlinedInput,
  FormControlLabel,
  Switch,
  Divider,
  Alert,
} from '@mui/material';
import { WaUpload } from '../whatsappPreview';
import { FlowComponent } from '../../../../flow-core/FlowComponent';
import {
  DocumentPickerCore,
  ALLOWED_MIME_TYPES,
  MAX_FILE_SIZE_KB,
  MAX_UPLOADED_DOCUMENTS,
  MAX_LABEL,
  MAX_DESCRIPTION,
} from '../../../../flow-core/components/DocumentPickerCore';

/**
 * Readable names for Meta's mime types. The VALUE is what goes in the Flow JSON and must stay
 * exact; this is only what the person building the form reads.
 */
const MIME_LABELS = {
  'application/pdf': 'PDF',
  'application/msword': 'Word (.doc)',
  'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word (.docx)',
  'application/vnd.ms-excel': 'Excel (.xls)',
  'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel (.xlsx)',
  'application/vnd.ms-powerpoint': 'PowerPoint (.ppt)',
  'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint (.pptx)',
  'application/vnd.oasis.opendocument.text': 'OpenDocument text',
  'application/vnd.oasis.opendocument.spreadsheet': 'OpenDocument spreadsheet',
  'application/vnd.oasis.opendocument.presentation': 'OpenDocument presentation',
  'application/zip': 'ZIP archive',
  'application/x-7z-compressed': '7z archive',
  'application/gzip': 'GZIP archive',
  'text/plain': 'Plain text',
  'image/jpeg': 'JPEG image',
  'image/png': 'PNG image',
  'image/gif': 'GIF image',
  'image/webp': 'WebP image',
  'image/heic': 'HEIC image',
  'image/heif': 'HEIF image',
  'image/avif': 'AVIF image',
  'image/tiff': 'TIFF image',
  'video/mp4': 'MP4 video',
  'video/mpeg': 'MPEG video',
};

const labelFor = (mime) => MIME_LABELS[mime] || mime;

export const DocumentPickerComponent = new FlowComponent({
  ...DocumentPickerCore,
  renderPreview(properties) {
    return (
      <WaUpload
        kind="document"
        label={properties.label || 'Upload documents'}
        minimum={properties['min-uploaded-documents']}
        maximum={properties['max-uploaded-documents']}
        description={properties.description}
        properties={properties}
      />
    );
  },
  renderPropertyEditor(properties, onChange) {
    const chosen = Array.isArray(properties['allowed-mime-types']) ? properties['allowed-mime-types'] : [];

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Alert severity="info">
          Lets someone attach files, a PDF, a spreadsheet, a photo of a bill. One upload question
          per screen, and a screen cannot have both this and a photo question.
        </Alert>

        <TextField
          fullWidth
          label="Field Name"
          value={properties.name || ''}
          onChange={(e) => onChange('name', e.target.value)}
          required
          size="small"
          helperText="Used to identify this field in the form data"
        />

        <TextField
          fullWidth
          label="Label"
          value={properties.label || ''}
          onChange={(e) => onChange('label', e.target.value)}
          required
          size="small"
          inputProps={{ maxLength: MAX_LABEL }}
          helperText={`${(properties.label || '').length}/${MAX_LABEL} characters`}
        />

        <TextField
          fullWidth
          label="Description"
          value={properties.description || ''}
          onChange={(e) => onChange('description', e.target.value)}
          size="small"
          multiline
          inputProps={{ maxLength: MAX_DESCRIPTION }}
          helperText={`${(properties.description || '').length}/${MAX_DESCRIPTION} characters`}
        />

        <FormControl fullWidth size="small">
          <InputLabel>Accepted file types</InputLabel>
          <Select
            multiple
            value={chosen}
            onChange={(e) => onChange('allowed-mime-types',
              typeof e.target.value === 'string' ? e.target.value.split(',') : e.target.value)}
            input={<OutlinedInput label="Accepted file types" />}
            renderValue={(selected) => (selected.length === 0
              ? 'Any supported file'
              : selected.map(labelFor).join(', '))}
          >
            {ALLOWED_MIME_TYPES.map((mime) => (
              <MenuItem key={mime} value={mime}>
                <Checkbox checked={chosen.indexOf(mime) > -1} size="small" />
                <ListItemText primary={labelFor(mime)} secondary={mime} />
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Divider sx={{ my: 1 }} />
        <Typography variant="subtitle2" color="text.secondary">
          Upload limits
        </Typography>

        <TextField
          fullWidth
          type="number"
          label="Min documents"
          value={properties['min-uploaded-documents'] ?? ''}
          onChange={(e) => onChange('min-uploaded-documents', e.target.value)}
          size="small"
          inputProps={{ min: 0, max: MAX_UPLOADED_DOCUMENTS }}
          helperText="0 makes the question optional"
        />

        <TextField
          fullWidth
          type="number"
          label="Max documents"
          value={properties['max-uploaded-documents'] ?? ''}
          onChange={(e) => onChange('max-uploaded-documents', e.target.value)}
          size="small"
          inputProps={{ min: 1, max: MAX_UPLOADED_DOCUMENTS }}
          helperText="Left blank, the flow allows 5, WhatsApp refuses a flow that could return more than 10 files in total"
        />

        <TextField
          fullWidth
          type="number"
          label="Max file size (KB)"
          value={properties['max-file-size-kb'] ?? ''}
          onChange={(e) => onChange('max-file-size-kb', e.target.value)}
          size="small"
          inputProps={{ min: 1, max: MAX_FILE_SIZE_KB }}
          helperText={`Left blank, the flow allows 8192 KB. WhatsApp's ceiling is ${MAX_FILE_SIZE_KB} KB`}
        />

        <Divider sx={{ my: 1 }} />

        <FormControlLabel
          control={(
            <Switch
              checked={properties.enabled !== false}
              onChange={(e) => onChange('enabled', e.target.checked)}
            />
          )}
          label="Enabled"
        />

        <FormControlLabel
          control={(
            <Switch
              checked={properties.visible !== false}
              onChange={(e) => onChange('visible', e.target.checked)}
            />
          )}
          label="Visible"
        />
      </Box>
    );
  },
});
